// EditSoapNoteModal.tsx
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, CancelButton } from 'molecules/Button';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { useFormSubmitWithLoading } from 'utils/hooks/useFormSubmitWithLoading';
import axiosService from 'utils/api/axios';
import RichTextEditor, { EditorValue } from 'react-rte';
import Modal from 'molecules/ConfirmModalTemplate';

const ValidationSchema = yup.object().shape({
    templateData: yup.string().required('required'),
    SoapNoteImages: yup.array().of(yup.string().min(10)).default([])
});

export type EditFormValues = {
    templateData: string;        // html
    SoapNoteImages: string[];    // image URLs (uploaded)
};

export type EditSoapFormProps = {
    onSubmit: (values: EditFormValues, formikHelpers?: FormikHelpers<EditFormValues>) => void;
    onClose: () => void;
    open: boolean;
    appointmentDetails?: any;
};

/* ----------------- Helpers ----------------- */

// Decode common HTML entities and any remaining ones.
function decodeEntities(str: string = ''): string {
    if (!str) return '';
    let out = str
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    if (/[&][a-zA-Z#0-9]+;/.test(out)) {
        const ta = document.createElement('textarea');
        ta.innerHTML = out;
        out = ta.value;
    }
    return out;
}

// DOM-based cleaner: removes leading "data:" that appears at the start of lines or inside common block tags.
function stripDataDOM(html = ''): string {
    const decoded = decodeEntities(html);
    const doc = new DOMParser().parseFromString(decoded, 'text/html');

    // Remove "data:" at the start of text inside block elements
    doc.querySelectorAll('p,div,li,h1,h2,h3,h4,h5,h6').forEach((el) => {
        const first = el.firstChild;
        if (first && first.nodeType === Node.TEXT_NODE) {
            first.nodeValue = (first.nodeValue || '').replace(/^\s*(?:data:|data:&nbsp;)\s*/i, '');
        }
    });

    // Also handle raw lines outside elements
    doc.body.innerHTML = doc.body.innerHTML.replace(/(^|\r?\n|\r)\s*data:\s*/gi, '$1');
    return doc.body.innerHTML;
}

// Upload a File and get a URL back from API
const uploadFile = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axiosService.post('users/upload/image', fd);
    return data?.data?.url || '';
};

/* --------------- Component --------------- */

const EditSoapNoteModal: FC<EditSoapFormProps> = ({
    onSubmit,
    onClose,
    open,
    appointmentDetails
}) => {
    // take from SOAP or Template, whichever exists
    const raw = useMemo(
        () =>
            (appointmentDetails?.soapNote && String(appointmentDetails.soapNote)) ||
            (appointmentDetails?.soapNoteTemplate && String(appointmentDetails.soapNoteTemplate)) ||
            '',
        [appointmentDetails?.soapNote, appointmentDetails?.soapNoteTemplate]
    );

    // normalize: decode + remove 'data:' noise
    const initialHtml = useMemo(() => stripDataDOM(raw), [raw]);

    // existing images if any (support either array of strings or array of {url} objects)
    const initialImages: string[] = useMemo(() => {
        const imgs = appointmentDetails?.soapNoteImages;
        if (!imgs) return [];
        if (Array.isArray(imgs)) {
            // accept strings or objects with url/src
            return imgs.map((it: any) => (typeof it === 'string' ? it : it?.url || it?.src)).filter(Boolean);
        }
        return [];
    }, [appointmentDetails?.soapNoteImages]);

    // RTE state
    const [rteValue, setRteValue] = useState<EditorValue>(
        RichTextEditor.createValueFromString(initialHtml, 'html')
    );

    // images state mirrors Formik field (for previews/snappy UI)
    const [images, setImages] = useState<string[]>(initialImages);

    // file input ref for attach button
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);

    // when modal opens or content changes, refresh editor & images
    useEffect(() => {
        if (open) {
            setRteValue(RichTextEditor.createValueFromString(initialHtml, 'html'));
            setImages(initialImages);
        }
    }, [open, initialHtml, initialImages]);

    const { onSubmitHandler, loading } = useFormSubmitWithLoading(onSubmit);
    const isScheduled = appointmentDetails?.appointmentStatus === 'SCHEDULED';

    return (
        <Modal
            headerTextId="provider.soapnote"
            open={open}
            // @ts-ignore
            onClose={onClose}
        >
            <Formik<EditFormValues>
                initialValues={{ templateData: initialHtml, SoapNoteImages: initialImages }}
                validationSchema={ValidationSchema}
                onSubmit={onSubmitHandler}
                enableReinitialize
            >
                {({ setFieldValue, values }) => (
                    <Form className="bg-white sm:px-2" style={{ height: '85vh', overflow: 'hidden' }}>
                        {/* Bounded, scrollable modal body */}
                        <div className="flex flex-col" style={{ height: '100%', minHeight: 0 }}>
                            {/* Sticky toolbar row (right side holds attach) */}
                            <div className="flex items-center justify-between gap-2 py-2">
                                <div className="text-sm font-medium opacity-70 px-1">SOAP Note</div>

                                {isScheduled && (
                                    <div className="flex items-center gap-2">
                                        {/* Attach images button */}
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            title="Attach images"
                                        >
                                            <span aria-hidden>📎</span>
                                            {uploading ? 'Uploading…' : 'Attach Images'}
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            hidden
                                            onChange={async (e) => {
                                                const files = Array.from(e.currentTarget.files || []);
                                                if (!files.length) return;
                                                setUploading(true);
                                                try {
                                                    const uploaded: string[] = [];
                                                    for (const f of files) {
                                                        try {
                                                            const url = await uploadFile(f);
                                                            if (url) uploaded.push(url);
                                                        } catch {}
                                                    }
                                                    if (uploaded.length) {
                                                        const next = [...images, ...uploaded];
                                                        setImages(next);
                                                        setFieldValue('SoapNoteImages', next, false);
                                                    }
                                                } finally {
                                                    setUploading(false);
                                                    // reset input so same files can be selected again
                                                    e.currentTarget.value = '';
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Scrollable editor area, sticky internal toolbar */}
                            <div className="flex-1 pr-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                                <style>
                                    {`
                    .RichTextEditor__toolbar {
                      position: sticky;
                      top: 0;
                      z-index: 10;
                      background: white;
                    }
                  `}
                                </style>

                                <RichTextEditor
                                    className="rte-root"
                                    editorClassName=""
                                    editorStyle={{ minHeight: 320 }}
                                    readOnly={!isScheduled}
                                    value={rteValue}
                                    onChange={(val) => {
                                        setRteValue(val);
                                        const html = val.toString('html');
                                        const cleaned = stripDataDOM(html);
                                        setFieldValue('templateData', cleaned, false);
                                    }}
                                />

                                {/* ---- Images appended at the end of the template area ---- */}
                                <div className="mt-4 border-t border-gray-100 pt-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-sm font-medium">Attached Images</div>
                                        {isScheduled && images.length > 0 && (
                                            <button
                                                type="button"
                                                className="text-xs underline opacity-70 hover:opacity-100"
                                                onClick={() => {
                                                    setImages([]);
                                                    setFieldValue('SoapNoteImages', [], false);
                                                }}
                                            >
                                                Remove all
                                            </button>
                                        )}
                                    </div>

                                    {images.length === 0 ? (
                                        <div className="text-xs text-gray-500">No images attached.</div>
                                    ) : (
                                        <div className="grid gap-3"
                                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
                                            {images.map((src, idx) => (
                                                <div key={idx} className="relative border rounded-md overflow-hidden">
                                                    {/* thumb */}
                                                    <img
                                                        src={src}
                                                        alt={`soap-img-${idx + 1}`}
                                                        style={{ width: '100%', height: 100, objectFit: 'cover' }}
                                                    />
                                                    {/* index badge */}
                                                    <div
                                                        className="absolute top-1 left-1 text-[11px] px-1.5 py-0.5 rounded bg-white/90 border"
                                                        title={`Image ${idx + 1}`}
                                                    >
                                                        #{idx + 1}
                                                    </div>
                                                    {/* remove */}
                                                    {isScheduled && (
                                                        <button
                                                            type="button"
                                                            title="Remove"
                                                            className="absolute top-1 right-1 text-xs bg-white/90 border rounded px-1.5 py-0.5 hover:bg-white"
                                                            onClick={() => {
                                                                const next = images.filter((_, i) => i !== idx);
                                                                setImages(next);
                                                                setFieldValue('SoapNoteImages', next, false);
                                                            }}
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            {isScheduled ? (
                                <div className="pt-2 border-t border-gray-100 flex sm:flex-row flex-col justify-center gap-3 sm:gap-6">
                                    <Button
                                        type="submit"
                                        loading={loading}
                                        textid="save"
                                        className="px-0 justify-center w-auto sm:px-16 sm:py-3"
                                    />
                                    <CancelButton
                                        type="button"
                                        textid="cancel"
                                        onClick={onClose}
                                        className="px-0 justify-center w-auto sm:px-16 sm:py-3"
                                    />
                                </div>
                            ) : (
                                <div className="pt-2 border-t border-gray-100 flex justify-center">
                                    <CancelButton
                                        type="button"
                                        textid="close"
                                        onClick={onClose}
                                        className="px-0 justify-center w-auto sm:px-16 sm:py-3"
                                    />
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default EditSoapNoteModal;
